import { useRef, useState, useEffect } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Coffee, Calendar, Clock, Sparkles, ArrowRight, Heart, Flower2, TreePine, Egg, Award, Phone } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { brunchService, Brunch as BrunchType } from '../services/brunchService'
import { getEventImageUrl } from '../config/api'

// Icon Mapping
const iconMap: Record<string, any> = {
  Heart,
  Flower2,
  TreePine,
  Egg,
  Coffee
}

// Kategorien für Brunches
const categories = [
  {
    id: 'ostern',
    title: 'Ostern',
    subtitle: 'Frühlingserwachen',
    icon: Egg,
    iconName: 'Egg',
    color: 'from-brand-500/20 to-brand-600/20',
    borderColor: 'border-brand-500/30',
    textColor: 'text-brand-400',
    bgColor: 'bg-brand-800/10',
    image: '/assets/brunch/seite/ostern_brunchseite.jpg',
    description: 'Feiern Sie mit uns den Frühling bei unserem festlichen Osterbrunch'
  },
  {
    id: 'muttertag',
    title: 'Muttertag',
    subtitle: 'Blütentraum',
    icon: Flower2,
    iconName: 'Flower2',
    color: 'from-brand-500/20 to-brand-600/20',
    borderColor: 'border-brand-500/30',
    textColor: 'text-brand-400',
    bgColor: 'bg-brand-800/10',
    image: '/assets/brunch/seite/muttertag_brunchseite.jpg',
    description: 'Ein besonderer Tag zu Ehren aller Mütter mit exklusivem Brunch'
  },
  {
    id: 'weihnachten',
    title: 'Weihnachten',
    subtitle: 'Winterzauber',
    icon: TreePine,
    iconName: 'TreePine',
    color: 'from-brand-500/20 to-brand-600/20',
    borderColor: 'border-brand-500/30',
    textColor: 'text-brand-400',
    bgColor: 'bg-brand-800/10',
    image: '/assets/brunch/seite/weihnachten_brunchseite.jpg',
    description: 'Festliche Stimmung und kulinarische Höhepunkte zur Weihnachtszeit'
  },
  {
    id: 'silvester',
    title: 'Silvester',
    subtitle: 'Jahreswechsel',
    headerSuffix: 'Dinnerbuffet',
    icon: Coffee,
    iconName: 'Coffee',
    color: 'from-brand-500/20 to-brand-600/20',
    borderColor: 'border-brand-500/30',
    textColor: 'text-brand-400',
    bgColor: 'bg-brand-800/10',
    image: '/assets/brunch/seite/silvester_brunchseite.jpg',
    description: 'Starten Sie genussvoll ins neue Jahr mit unserem Silvester-Dinner'
  }
]

// Features
const features = [
  { icon: '🍽️', title: 'Premium Buffet', desc: 'Reichhaltiges Angebot an frischen, regionalen Spezialitäten' },
  { icon: '🏛️', title: 'Historisches Ambiente', desc: 'Genießen in den stilvollen Räumen unseres Landguts' },
  { icon: '⭐', title: 'Erstklassiger Service', desc: 'Unser Team sorgt für einen unvergesslichen Ablauf' }
]

export default function Brunch() {
  const heroRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  const [brunches, setBrunches] = useState<BrunchType[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasTriedLoading, setHasTriedLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  // Brunches erst laden, wenn wirklich benötigt (z.B. beim Klick auf eine Kategorie). Gibt die geladenen Daten zurück.
  const ensureBrunchesLoaded = async (): Promise<BrunchType[]> => {
    if (hasTriedLoading) return brunches
    if (isLoading) return []

    setIsLoading(true)
    try {
      const data = await brunchService.getAllBrunches()
      setBrunches(data)
      setHasTriedLoading(true)
      return data
    } catch (error) {
      console.error('Brunches konnten nicht geladen werden, zeige nur statische Kategorien an', error)
      setBrunches([])
      setHasTriedLoading(true)
      return []
    } finally {
      setIsLoading(false)
    }
  }

  // Brunches nach Kategorie gruppieren
  const getBrunchesByCategory = (iconName: string) => {
    return brunches.filter(brunch => brunch.icon_name === iconName)
  }

  // Prüfen ob eine Kategorie Brunches hat
  const categoryHasBrunches = (iconName: string) => {
    // Bevor wir Daten geladen haben, nehmen wir an, dass potenziell Events existieren,
    // damit die Kategorien anklickbar bleiben.
    if (!hasTriedLoading) return true
    return brunches.some(brunch => brunch.icon_name === iconName)
  }

  // Nach oben scrollen, wenn eine Kategorie ausgewählt wird oder zurückgegangen wird
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [selectedCategory])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/brunchero.jpeg"
            alt="Brunch bei Hückels May"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
        </motion.div>

        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Coffee className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Kulinarische Highlights</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            Unsere <span className="italic text-white/70">Brunch Events</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/50 text-xs sm:text-sm md:text-base max-w-xl leading-relaxed px-4"
          >
            Vier exklusive Brunch-Events im Jahr – jedes einzigartig, jedes unvergesslich.
          </motion.p>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          KATEGORIE AUSWAHL
      ════════════════════════════════════════════════════════════ */}
      {!selectedCategory && (
        <section className="relative py-24 md:py-32">
          <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
          
          <div className="max-w-[1400px] mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
              >
                Unsere Events
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white font-light"
              >
                <span className="italic text-white">Wann möchten Sie</span> <span className="italic text-white/60">uns besuchen?</span>
              </motion.h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {categories.map((category, index) => {
                const Icon = category.icon
                const hasBrunches = categoryHasBrunches(category.iconName)
                const brunchCount = getBrunchesByCategory(category.iconName).length

                return (
                    <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <div
                      onClick={async () => {
                        const data = await ensureBrunchesLoaded()
                        const categoryBrunches = data.filter(b => b.icon_name === category.iconName)
                        if (categoryBrunches.length === 1) {
                          navigate(`/brunch/${categoryBrunches[0].slug}`)
                          return
                        }
                        setSelectedCategory(category.id)
                      }}
                      className={`group block h-full cursor-pointer`}
                    >
                      <div className={`relative h-full rounded-2xl overflow-hidden border ${category.borderColor} hover:border-opacity-60 transition-all duration-500 bg-[#111]`}>
                        
                        {/* Bild */}
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={category.image}
                            alt={category.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            onError={(e) => {
                              e.currentTarget.src = '/assets/brunch/brunch_vorschau_fallback.jpg'
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/40 to-transparent" />
                          
                          {/* Icon Badge */}
                          <div className={`absolute top-4 left-4 w-12 h-12 ${category.bgColor} backdrop-blur-xl rounded-xl border ${category.borderColor} flex items-center justify-center`}>
                            <Icon className={`w-6 h-6 ${category.textColor}`} />
                          </div>
                          
                          {/* Event Count Badge */}
                          {hasBrunches && hasTriedLoading && (
                            <div className={`absolute bottom-4 left-4 px-3 py-1.5 ${category.bgColor} backdrop-blur-xl rounded-full border ${category.borderColor}`}>
                              <div className="flex items-center gap-2">
                                <div className={`w-1.5 h-1.5 rounded-full ${category.textColor.replace('text-', 'bg-')} animate-pulse`} />
                                <span className={`${category.textColor} text-xs`}>{brunchCount} {brunchCount === 1 ? 'Event' : 'Events'}</span>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="p-6">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className={`${category.textColor} text-[10px] uppercase tracking-[0.25em] mb-1 opacity-60`}>{category.subtitle}</p>
                              <h3 className="font-serif text-2xl text-white font-light">{category.title}</h3>
                            </div>
                            <div className={`w-10 h-10 rounded-full border ${category.borderColor} flex items-center justify-center transition-all duration-500 group-hover:${category.bgColor}`}>
                              <ArrowRight className={`w-4 h-4 ${category.textColor} opacity-40 group-hover:opacity-100 transition-opacity`} />
                            </div>
                          </div>
                          
                          <p className="text-white/50 text-sm leading-relaxed mb-4">
                            {category.description}
                          </p>
                          
                          <div className="flex items-center gap-2 text-white/40 text-sm group-hover:text-white/60 transition-colors">
                            <span>Events ansehen</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════════════════════════
          BRUNCHES DER AUSGEWÄHLTEN KATEGORIE
      ════════════════════════════════════════════════════════════ */}
      {selectedCategory && (
        <section className="relative py-24 md:py-32">
          <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
          
          <div className="max-w-[1400px] mx-auto px-6 relative z-10">
            {/* Back Button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => setSelectedCategory(null)}
              className="mb-12 flex items-center gap-2 text-white/60 hover:text-white transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                <ArrowRight className="w-4 h-4 rotate-180" />
              </div>
              <span className="text-sm uppercase tracking-[0.2em]">Zurück zur Auswahl</span>
            </motion.button>

            {/* Category Header */}
            {(() => {
              const category = categories.find(c => c.id === selectedCategory)
              if (!category) return null
              const Icon = category.icon
              
              return (
                <div className="text-center mb-16">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl ${category.bgColor} border ${category.borderColor} mb-6`}
                  >
                    <Icon className={`w-10 h-10 ${category.textColor}`} />
                  </motion.div>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light mb-4"
                  >
                    {category.title} <span className="italic text-white/60">{category.headerSuffix ?? 'Brunches'}</span>
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-white/50 text-base max-w-2xl mx-auto"
                  >
                    {category.description}
                  </motion.p>
                </div>
              )
            })()}

            {/* Brunches Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {(() => {
                const category = categories.find(c => c.id === selectedCategory)
                if (!category) return null
                const categoryBrunches = getBrunchesByCategory(category.iconName)
                
                return categoryBrunches.map((brunch, index) => {
                  const Icon = iconMap[brunch.icon_name] || Coffee
                  return (
                    <motion.div
                      key={brunch.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="h-full"
                    >
                      <Link to={`/brunch/${brunch.slug}`} className="group block h-full">
                        <div className="relative h-full min-h-[600px] rounded-2xl overflow-hidden bg-white/[0.02] border border-white/5 hover:border-brand-800/30 transition-all duration-500 flex flex-col">
                          
                          {/* Image */}
                          <div className="relative h-[250px] flex-shrink-0 overflow-hidden">
                            <img 
                              src={getEventImageUrl(brunch.preview_image_url) || '/assets/brunch/brunch_vorschau_fallback.jpg'} 
                              alt={brunch.title}
                              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                              loading="lazy"
                              onError={(e) => {
                                if (e.currentTarget.src !== '/assets/brunch/brunch_vorschau_fallback.jpg') {
                                  e.currentTarget.src = '/assets/brunch/brunch_vorschau_fallback.jpg'
                                }
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                          </div>

                          {/* Content */}
                          <div className="p-6 flex-1 flex flex-col">
                            {/* Badge */}
                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 ${category.bgColor} border ${category.borderColor} rounded-full self-start mb-4`}>
                              <div className={`w-1.5 h-1.5 rounded-full ${category.textColor.replace('text-', 'bg-')} animate-pulse`} />
                              <span className={`${category.textColor} text-xs uppercase tracking-[0.2em]`}>{category.title}</span>
                            </div>

                            {/* Icon & Title */}
                            <div className="flex-1 flex flex-col">
                              <div className={`w-12 h-12 rounded-xl ${category.bgColor} border ${category.borderColor} flex items-center justify-center mb-4`}>
                                <Icon className={`w-6 h-6 ${category.textColor}`} />
                              </div>

                              <div className={`h-px w-10 ${category.borderColor.replace('border-', 'bg-')} mb-4 group-hover:w-16 transition-all duration-500`} />
                              
                              <h3 className="font-serif text-2xl md:text-3xl text-white font-light mb-2">
                                {brunch.title}
                              </h3>
                              <p className={`${category.textColor} opacity-60 text-sm uppercase tracking-[0.2em] mb-4`}>
                                {brunch.subtitle}
                              </p>
                              
                              {brunch.description && (
                                <p className="text-white/60 text-sm mb-4 line-clamp-2">
                                  {brunch.description}
                                </p>
                              )}
                            </div>

                            {/* Date & CTA */}
                            <div className="space-y-4 mt-auto">
                              <div className="flex items-center justify-between p-3 bg-white/[0.02] border border-white/5 rounded-xl">
                                <div className="flex items-center gap-2">
                                  <Calendar className={`w-4 h-4 ${category.textColor}`} />
                                  <span className="text-white text-sm">
                                    {new Date(brunch.event_date).toLocaleDateString('de-DE', {
                                      day: '2-digit',
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className={`w-4 h-4 ${category.textColor}`} />
                                  <span className="text-white/60 text-sm">{brunch.event_time}</span>
                                </div>
                              </div>
                              
                              {brunch.price && (
                                <div className={`flex items-center gap-2 px-3 py-2 ${category.bgColor} border ${category.borderColor} rounded-xl`}>
                                  <span className={`${category.textColor} text-sm font-semibold`}>{brunch.price}</span>
                                </div>
                              )}

                              <div className="flex items-center justify-between">
                                <span className="text-white/40 text-sm group-hover:text-white/60 transition-colors">
                                  Details entdecken
                                </span>
                                <div className={`w-10 h-10 rounded-full ${category.bgColor} flex items-center justify-center group-hover:opacity-80 transition-opacity`}>
                                  <ArrowRight className={`w-4 h-4 ${category.textColor}`} />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  )
                })
              })()}
            </div>
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════════════════════════
          FEATURES
      ════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
            >
              Das Besondere
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white font-light"
            >
              Was unsere Brunches <span className="italic text-white/60">besonders macht</span>
            </motion.h2>
          </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {features.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group p-8 bg-white/[0.02] border border-white/5 rounded-2xl hover:border-brand-800/30 transition-all duration-500"
              >
                <div className="text-5xl mb-6">{item.icon}</div>
                <h3 className="font-serif text-xl text-white mb-3 group-hover:text-brand-400 transition-colors">
                  {item.title}
                </h3>
                <p className="text-white/40 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Heart className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Reservieren</p>
            
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-8 leading-tight">
              Sichern Sie sich<br />
              <span className="italic text-white/60">Ihren Tisch</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Unsere Brunch-Events sind schnell ausgebucht. Reservieren Sie jetzt für ein unvergessliches Genusserlebnis.
            </p>

            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 md:px-8 lg:px-10 py-3 md:py-4 bg-white text-black text-xs md:text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-2 md:gap-3 mx-auto"
              >
                <Phone className="w-4 h-4" />
                Jetzt reservieren
              </motion.button>
            </Link>
            
            <p className="mt-8 text-white/30 text-xs uppercase tracking-[0.2em]">
              Auch telefonisch: 01575 5537615
            </p>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
